import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Wrench, Plus, PencilSimple, Trash, MagnifyingGlass,
  ShieldCheck, Tag, Sparkle, X, WarningCircle, CheckCircle, Clock, FileText, CaretRight
} from "@phosphor-icons/react";

const CATEGORY_MAP = {
  all: "All Categories",
  warranty: "Warranty",
  amc: "AMC",
  on_demand: "On Demand",
  installation: "Installation & Reg",
  other: "Other"
};

const CATEGORY_BADGES = {
  warranty: { label: "Warranty", bg: "bg-blue-50 text-[#002FA7] border-blue-200" },
  amc: { label: "AMC", bg: "bg-purple-50 text-purple-700 border-purple-200" },
  on_demand: { label: "On Demand", bg: "bg-amber-50 text-amber-800 border-amber-200" },
  installation: { label: "Installation", bg: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  other: { label: "Other", bg: "bg-gray-100 text-gray-700 border-gray-200" },
  general: { label: "General", bg: "bg-gray-100 text-gray-700 border-gray-200" }
};

function generateSlug(text) {
  return (text || "")
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s-]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function generateNextCode(types) {
  const codes = (types || [])
    .map((t) => Number(t.code))
    .filter((n) => !isNaN(n) && n > 0);
  if (codes.length === 0) return 101;
  return Math.max(...codes) + 1;
}

export default function ServiceTypeMaster() {
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all"); // 'all' | 'active' | 'inactive'
  const [modalOpen, setModalOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchServiceTypes = async () => {
    try {
      setLoading(true);
      const res = await api.get("/service-types");
      setServiceTypes(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load service types");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServiceTypes();
  }, []);

  const filtered = useMemo(() => {
    return serviceTypes.filter((st) => {
      const matchSearch =
        (st.name || "").toLowerCase().includes(search.toLowerCase()) ||
        (st.key || "").toLowerCase().includes(search.toLowerCase()) ||
        String(st.code ?? "").toLowerCase().includes(search.toLowerCase()) ||
        (st.description || "").toLowerCase().includes(search.toLowerCase()) ||
        (st.category || "").toLowerCase().includes(search.toLowerCase());

      if (!matchSearch) return false;

      if (categoryFilter !== "all" && st.category !== categoryFilter) return false;

      if (statusFilter === "active" && !st.is_active) return false;
      if (statusFilter === "inactive" && st.is_active) return false;

      return true;
    });
  }, [serviceTypes, search, categoryFilter, statusFilter]);

  const totalContractsLinked = useMemo(() => {
    return serviceTypes.reduce((acc, st) => acc + (st.contracts_count || 0), 0);
  }, [serviceTypes]);

  const inUseCount = useMemo(() => {
    return serviceTypes.filter((st) => (st.contracts_count || 0) > 0).length;
  }, [serviceTypes]);

  const handleOpenAdd = () => {
    setEditingType(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (st) => {
    setEditingType(st);
    setModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await api.delete(`/service-types/${deleteConfirm.id}`);
      toast.success(`Service Type "${deleteConfirm.name}" deleted successfully`);
      setDeleteConfirm(null);
      fetchServiceTypes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete service type");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="service-type-master-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-[#E5E7EB] pb-6">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#6B7280] mb-1">
            <span>Master</span>
            <span>/</span>
            <span className="text-[#002FA7]">Service Type Master</span>
          </div>
          <h1 className="font-heading font-black text-2xl sm:text-3xl tracking-tight text-[#111827]">
            Service Type Master
          </h1>
          <p className="text-sm text-[#4B5563] mt-1">
            Configure the 7 service and contract types, validities, categories, and automated reminder scopes.
          </p>
        </div>

        <button
          onClick={handleOpenAdd}
          data-testid="add-service-type-btn"
          className="bg-[#002FA7] text-white font-bold px-4 py-2.5 text-sm hover:bg-[#002280] transition-colors flex items-center justify-center gap-2 shadow-sm shrink-0"
        >
          <Plus size={18} weight="bold" />
          <span>Add Service Type</span>
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E5E7EB] p-4 flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-50 text-[#002FA7] flex items-center justify-center rounded-lg">
            <Wrench size={26} weight="duotone" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#6B7280]">Total Service Types</div>
            <div className="text-2xl font-black text-[#111827]">{serviceTypes.length} Types</div>
          </div>
        </div>

        <div className="bg-white border border-[#E5E7EB] p-4 flex items-center gap-4">
          <div className="w-12 h-12 bg-emerald-50 text-emerald-600 flex items-center justify-center rounded-lg">
            <ShieldCheck size={26} weight="duotone" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#6B7280]">In Active Use</div>
            <div className="text-2xl font-black text-[#111827]">
              {inUseCount} <span className="text-xs font-normal text-[#6B7280]">of {serviceTypes.length}</span>
            </div>
          </div>
        </div>

        <div className="bg-white border border-[#E5E7EB] p-4 flex items-center gap-4">
          <div className="w-12 h-12 bg-purple-50 text-purple-700 flex items-center justify-center rounded-lg">
            <FileText size={26} weight="duotone" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#6B7280]">Linked Contracts</div>
            <div className="text-2xl font-black text-[#111827]">{totalContractsLinked} Records</div>
          </div>
        </div>
      </div>

      {/* Controls & Search */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-white p-3 border border-[#E5E7EB]">
        {/* Search */}
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search service type by name, code, key, or category..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 text-xs font-bold border border-gray-200 outline-none focus:border-[#002FA7]"
          />
        </div>

        {/* Category Pills */}
        <div className="flex flex-wrap items-center gap-1.5 text-xs font-bold">
          {Object.entries(CATEGORY_MAP).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setCategoryFilter(k)}
              className={`px-2.5 py-1 text-xs font-bold border transition-colors ${
                categoryFilter === k
                  ? "bg-[#002FA7] text-white border-[#002FA7]"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Status filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-[#D1D5DB] px-3 py-1.5 text-xs font-bold bg-white text-slate-800 outline-none focus:border-[#002FA7]"
        >
          <option value="all">All Status</option>
          <option value="active">Active Only</option>
          <option value="inactive">Inactive Only</option>
        </select>
      </div>

      {/* Table */}
      <div className="border border-[#E5E7EB] bg-white overflow-x-auto shadow-sm">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b-2 border-[#111827] bg-[#F9FAFB]">
              <th className="py-3 px-4 text-left text-[10px] uppercase tracking-widest font-bold whitespace-nowrap text-[#111827]">
                Code
              </th>
              <th className="py-3 px-4 text-left text-[10px] uppercase tracking-widest font-bold whitespace-nowrap text-[#111827]">
                Service Type & Identifier
              </th>
              <th className="py-3 px-4 text-left text-[10px] uppercase tracking-widest font-bold whitespace-nowrap text-[#111827]">
                Category
              </th>
              <th className="py-3 px-4 text-left text-[10px] uppercase tracking-widest font-bold whitespace-nowrap text-[#111827]">
                Description
              </th>
              <th className="py-3 px-4 text-left text-[10px] uppercase tracking-widest font-bold whitespace-nowrap text-[#111827]">
                Default Validity
              </th>
              <th className="py-3 px-4 text-left text-[10px] uppercase tracking-widest font-bold whitespace-nowrap text-[#111827]">
                Linked Contracts
              </th>
              <th className="py-3 px-4 text-left text-[10px] uppercase tracking-widest font-bold whitespace-nowrap text-[#111827]">
                Status
              </th>
              <th className="py-3 px-4 text-right text-[10px] uppercase tracking-widest font-bold whitespace-nowrap text-[#111827]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="py-12 text-center text-sm text-[#6B7280]">
                  Loading service types...
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-12 text-center text-sm text-[#6B7280]">
                  No service types match the selected filter.
                </td>
              </tr>
            ) : (
              filtered.map((st) => {
                const catBadge = CATEGORY_BADGES[st.category] || CATEGORY_BADGES.general;
                const hasContracts = (st.contracts_count || 0) > 0;

                return (
                  <tr
                    key={st.id}
                    className="border-b border-[#E5E7EB] hover:bg-[#F9FAFB] transition-colors"
                    data-testid={`service-type-row-${st.id}`}
                  >
                    {/* Code */}
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span className="font-mono text-xs font-bold px-2 py-0.5 bg-gray-100 text-gray-800 border border-gray-200">
                        {st.code}
                      </span>
                    </td>

                    {/* Name & Key */}
                    <td className="py-3 px-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="font-bold text-sm text-[#111827]">{st.name}</span>
                        <span className="font-mono text-[11px] text-[#6B7280]">{st.key}</span>
                      </div>
                    </td>

                    {/* Category */}
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span className={`px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider border ${catBadge.bg}`}>
                        {catBadge.label}
                      </span>
                    </td>

                    {/* Description */}
                    <td className="py-3 px-4 text-xs text-gray-600 max-w-xs truncate" title={st.description}>
                      {st.description || "—"}
                    </td>

                    {/* Validity */}
                    <td className="py-3 px-4 text-xs font-bold text-gray-800 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <Clock size={14} className="text-gray-400" />
                        <span>{st.default_validity_days || 365} Days</span>
                      </div>
                    </td>

                    {/* Linked Contracts */}
                    <td className="py-3 px-4 whitespace-nowrap">
                      {hasContracts ? (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-bold bg-blue-50 text-[#002FA7] border border-blue-200">
                          {st.contracts_count} Contracts
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400 font-mono">0 Linked</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider border ${
                          st.is_active
                            ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                            : "bg-red-50 text-red-700 border-red-200"
                        }`}
                      >
                        {st.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="py-3 px-4 text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleOpenEdit(st)}
                          data-testid={`edit-service-type-${st.id}`}
                          className="text-[#002FA7] hover:bg-blue-50 p-1.5 border border-transparent hover:border-blue-200 transition-colors"
                          title="Edit Service Type"
                        >
                          <PencilSimple size={15} weight="bold" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(st)}
                          disabled={hasContracts}
                          data-testid={`delete-service-type-${st.id}`}
                          className="text-[#EF4444] hover:bg-red-50 p-1.5 border border-transparent hover:border-red-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title={hasContracts ? "Cannot delete: contracts are linked" : "Delete Service Type"}
                        >
                          <Trash size={15} weight="bold" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Modal: Add / Edit */}
      {modalOpen && (
        <ServiceTypeModal
          serviceTypes={serviceTypes}
          editingType={editingType}
          onClose={() => setModalOpen(false)}
          onSuccess={() => {
            setModalOpen(false);
            fetchServiceTypes();
          }}
        />
      )}

      {/* Modal: Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white border border-[#111827] w-full max-w-md p-6 shadow-2xl space-y-4">
            <div className="flex items-center gap-3 text-red-600">
              <WarningCircle size={28} weight="fill" />
              <h3 className="font-heading font-black text-xl text-[#111827]">Delete Service Type?</h3>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">
              Are you sure you want to delete <span className="font-bold text-[#111827]">"{deleteConfirm.name}"</span> (Code: {deleteConfirm.code})? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="border border-[#D1D5DB] px-4 py-2 text-xs font-bold hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="bg-red-600 text-white text-xs font-bold px-4 py-2 hover:bg-red-700 disabled:opacity-60 transition-colors"
              >
                {deleting ? "Deleting..." : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ServiceTypeModal({ serviceTypes, editingType, onClose, onSuccess }) {
  const isEdit = !!editingType;
  const [name, setName] = useState(editingType ? editingType.name : "");
  const [code, setCode] = useState(editingType ? editingType.code : generateNextCode(serviceTypes));
  const [key, setKey] = useState(editingType ? editingType.key : "");
  const [category, setCategory] = useState(editingType ? editingType.category : "warranty");
  const [defaultValidity, setDefaultValidity] = useState(editingType ? editingType.default_validity_days || 365 : 365);
  const [description, setDescription] = useState(editingType ? editingType.description || "" : "");
  const [isActive, setIsActive] = useState(editingType ? editingType.is_active : true);
  const [saving, setSaving] = useState(false);

  const handleNameChange = (e) => {
    const val = e.target.value;
    setName(val);
    if (!isEdit) {
      setKey(generateSlug(val));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Service Type Name is required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        code: Number(code),
        key: (key || generateSlug(name)).trim(),
        category,
        default_validity_days: Number(defaultValidity),
        description: description.trim(),
        is_active: isActive
      };

      if (isEdit) {
        await api.patch(`/service-types/${editingType.id}`, payload);
        toast.success(`Service Type "${name}" updated successfully`);
      } else {
        await api.post("/service-types", payload);
        toast.success(`Service Type "${name}" created successfully`);
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save service type");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white border border-[#111827] w-full max-w-lg p-6 shadow-2xl space-y-4"
        onClick={(e) => e.stopPropagation()}
        data-testid="service-type-modal"
      >
        <div className="flex items-center justify-between border-b border-gray-100 pb-3">
          <h3 className="font-heading font-black text-2xl tracking-tighter text-[#111827]">
            {isEdit ? "Edit Service Type" : "Add Service Type"}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 text-gray-500">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280] block mb-1">
                Service Type Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={name}
                onChange={handleNameChange}
                placeholder="e.g. Warranty, AMC"
                data-testid="service-type-name-input"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280] block mb-1">
                Type Code (Numeric) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="e.g. 101"
                data-testid="service-type-code-input"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280] block mb-1">
                Identifier Key <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="e.g. warranty, amc"
                data-testid="service-type-key-input"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280] block mb-1">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                data-testid="service-type-category-select"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-sm bg-white font-medium focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
              >
                <option value="warranty">Warranty</option>
                <option value="amc">AMC (Annual Maintenance)</option>
                <option value="on_demand">On Demand / On Call</option>
                <option value="installation">Installation & Registration</option>
                <option value="other">Other / Custom</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280] block mb-1">
                Default Validity (Days)
              </label>
              <input
                type="number"
                value={defaultValidity}
                onChange={(e) => setDefaultValidity(e.target.value)}
                placeholder="365"
                data-testid="service-type-validity-input"
                className="w-full border border-[#D1D5DB] px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
              />
            </div>

            <div className="flex items-center pt-6">
              <label className="flex items-center gap-2 text-xs font-bold text-gray-800 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="w-4 h-4 text-[#002FA7] rounded"
                />
                <span>Active for Service CRM</span>
              </label>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280] block mb-1">
              Description & Reminders Purpose
            </label>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Standard OEM equipment warranty coverage with automated expiry reminders..."
              data-testid="service-type-desc-input"
              className="w-full border border-[#D1D5DB] px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
            />
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="border border-[#D1D5DB] px-5 py-2 text-sm font-bold hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="service-type-submit-btn"
              className="bg-[#002FA7] text-white font-bold px-5 py-2 text-sm hover:bg-[#002280] disabled:opacity-60 transition-colors"
            >
              {saving ? "Saving..." : isEdit ? "Update Service Type" : "Create Service Type"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
